// User type name values
export type UserTypeName = "All" | "MpVap" | "PlusCard";

// Backend response interface for user data
export interface IBackendUser {
  userId: string;
  firstName: string;
  lastName: string;
  givenName: string;
  email: string;
  isActive: boolean;
  createdOn: string;
  createdBy: string;
  roleName: string;
  roleId: number;
  userTypeName: UserTypeName;
  userTypeId: number;
}

// Frontend user interface for display
export interface IUserDisplay {
  email: string;
  isActive: boolean;
  roleName: string;
  userTypeName: UserTypeName;
  details: {
    userId: string;
    firstName: string;
    lastName: string;
    givenName: string;
    createdOn: string;
    createdBy: string;
    roleId: number;
    userTypeId: number;
  };
}

// Backend query parameters interface
export interface IBackendUserQueryParams {
  pageSize?: number;
  pageNumber?: number;
  correlationId?: string; // Note: Auto-injected by backendBaseQuery for POST requests
  email?: string;
  roleId?: number;
  userTypeId?: number;
  givenName?: string;
  isActive?: boolean | null;
}

// Backend API response interface (actual structure from server)
export interface IBackendUserApiResponse {
  data: IBackendUser[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

// Paginated response interface (frontend format)
export interface IPaginatedUserResponse {
  data: IUserDisplay[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Create user request interface
export interface ICreateUserRequest {
  firstName: string;
  lastName: string;
  givenName: string;
  userEmail: string;
  isActive: boolean;
  roleId: number;
  createdBy: string;
  userTypeId: number;
}

// Create user payload interface (with correlationId)
export interface ICreateUserPayload {
  createUserRequest: ICreateUserRequest;
  correlationId: string;
}

// Role interface for backend response
export interface IRole {
  id: number;
  name: string;
  description: string;
  createdOn: string;
  isActive: boolean;
}

// User Type interface for backend response
export interface IUserType {
  userTypeId: number;
  userTypeName: UserTypeName;
  description: string;
  createdOn: string;
}

// User logging activity query parameters interface
export interface IUserLoggingActivityQueryParams {
  pageSize?: number;
  pageNumber?: number;
  userEmail?: string;
  correlationId?: string; // Note: Auto-injected by backendBaseQuery for POST requests
}

// Single user logging activity record interface
export interface IUserLoggingActivity {
  userEmail: string;
  userType: string;
  userRole: string;
  userLogStatus: string;
  logMessage: string;
  logCreatedOn: string;
}

// User logging activity API response interface
export interface IUserLoggingActivityResponse {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  data: IUserLoggingActivity[];
}

// User create/permission audit log query parameters interface
export interface IUserAndAccessMgmtAuditLogQueryParams {
  pageSize?: number;
  pageNumber?: number;
  userEmail?: string;
  correlationId?: string; // Note: Auto-injected by backendBaseQuery for POST requests
}

// Single user create/permission audit log record interface
export interface IUserAndAccessMgmtAuditLog {
  logId: string;
  userName: string | null;
  userEmail: string | null;
  activityType: string | null;
  previousInfo: string | null;
  updatedInfo: string | null;
  actionMessage: string | null;
  actionTimestamp: string | null;
  actionByName: string | null;
}

// User create/permission audit log API response interface
export interface IUserAndAccessMgmtAuditLogResponse {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  data: IUserAndAccessMgmtAuditLog[];
}

// Helper function to transform backend data to frontend format
export const transformBackendUser = (
  backendUser: IBackendUser,
): IUserDisplay => ({
  email: backendUser.email,
  isActive: backendUser.isActive,
  roleName: backendUser.roleName,
  userTypeName: backendUser.userTypeName,
  details: {
    userId: backendUser.userId,
    firstName: backendUser.firstName,
    lastName: backendUser.lastName,
    givenName: backendUser.givenName,
    createdOn: backendUser.createdOn,
    createdBy: backendUser.createdBy,
    roleId: backendUser.roleId,
    userTypeId: backendUser.userTypeId,
  },
});
